import { Link, NavLink } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from '../state/auth.js';
import { useI18n, useT } from '../state/i18n.js';
import { ProfileMenu } from './ProfileMenu.js';
import { inboxAvailable } from '../api/inbox.js';

/** App-wide language toggle shown in the top bar. */
export function LangToggle(): JSX.Element {
  const { lang, setLang } = useI18n();
  return (
    <div className="lang-toggle" role="group" aria-label="Language">
      <button
        type="button"
        className={lang === 'ko' ? 'is-active' : ''}
        onClick={() => setLang('ko')}
        aria-pressed={lang === 'ko'}
      >
        한국어
      </button>
      <button
        type="button"
        className={lang === 'en' ? 'is-active' : ''}
        onClick={() => setLang('en')}
        aria-pressed={lang === 'en'}
      >
        EN
      </button>
    </div>
  );
}

export function Shell({ children }: { children: ReactNode }): JSX.Element {
  const auth = useAuth();
  const t = useT();
  return (
    <div className="shell">
      <a href="#main" className="skip-link">
        {t({ ko: '본문으로 건너뛰기', en: 'Skip to content' })}
      </a>
      <header className="topbar" role="banner">
        <Link to="/" className="brand" aria-label="CloudBTL home">
          CloudBTL
        </Link>
        <nav aria-label="Primary">
          <NavLink to="/app">{t({ ko: '업로드', en: 'Upload' })}</NavLink>
          <NavLink to="/lake">Smartlake</NavLink>
          <NavLink to="/documents">{t({ ko: '자료실', en: 'Documents' })}</NavLink>
          {inboxAvailable() && <NavLink to="/inbox">{t({ ko: '내 수신함', en: 'My inbox' })}</NavLink>}
          <NavLink to="/learn">{t({ ko: '배우기', en: 'Learn' })}</NavLink>
          <NavLink to="/plans">{t({ ko: '요금제', en: 'Plans' })}</NavLink>
          {auth.user?.isAdmin ? (
            <NavLink to="/analytics">{t({ ko: '분석', en: 'Analytics' })}</NavLink>
          ) : null}
        </nav>
        <div className="topbar-right">
          <LangToggle />
          {auth.user ? (
            <ProfileMenu />
          ) : (
            <Link to="/login" className="cta-secondary">
              {t({ ko: '로그인', en: 'Log in' })}
            </Link>
          )}
        </div>
      </header>
      <main id="main">{children}</main>
      <footer className="footer" role="contentinfo">
        <div className="footer-org" itemScope itemType="https://schema.org/Organization">
          <span itemProp="name">CloudBTL</span>
          <a href="mailto:hi@cloudbtl.com" itemProp="email">
            hi@cloudbtl.com
          </a>
        </div>
        <nav aria-label="Footer">
          <Link to="/">{t({ ko: '홈', en: 'Home' })}</Link>
          <Link to="/login">{t({ ko: '로그인', en: 'Sign in' })}</Link>
          <Link to="/plans">{t({ ko: '요금제', en: 'Plans' })}</Link>
          <Link to="/legal/terms">{t({ ko: '이용약관', en: 'Terms' })}</Link>
          <Link to="/legal/privacy">{t({ ko: '개인정보 처리방침', en: 'Privacy' })}</Link>
        </nav>
        <span className="footer-copy">© {new Date().getFullYear()} CloudBTL</span>
      </footer>
    </div>
  );
}
